import {createSelector} from "reselect/es"; // fix for Webstorm IDE code analysis
import {codeProjectFromArray, convertToDate, convertToLocalDate} from "../../projections";
import * as R from "ramda";
import moment from 'moment-timezone';
import {codeTables} from "../../codes";

export const formatMoment = (timestamp) => timestamp ? convertToDate(timestamp).tz(moment.tz.guess()).format('MMMM Do, YYYY @ h:mm A z') : '';
export const formatMomentDate = (timestamp) => timestamp ? convertToLocalDate(timestamp).tz(moment.tz.guess()).format('MMMM Do, YYYY') : '';

export const ReportSelectorFactory = (name) => {
  if (name === 'bid-status') return bidStatusSelector;
  if (name === 'upcoming-bids') return upcomingBidReportSelector;
  if (name === 'upcoming-bid-details') return upcomingBidDetailsReportSelector;
  if (name === 'projects') return projectsListSelector;
  if (name === 'project-status') return projectStatusSelector;
  if (name === 'task-status') return taskStatusSelector;
  if (name === 'task-details') return taskReportSelector;
  if (name === 'rfi-status') return projectRfiStatusSelector;
  if (name === 'rfi-details') return projectRfiReportSelector;
  if (name === 'submittal-status') return submittalStatusSelector;
  if (name === 'submittal-details') return submittalReportSelector;
  if (name === 'punchlist-status') return punchStatusSelector;
  if (name === 'daily-log-details') return dailyLogReportSelector;
  if (name === 'meeting-minutes-details') return meetingMinutesReportSelector;
  return null;
};

const statusSelector = (report_path, codes_path) => createSelector(
  state => report_path(state),
  state => codes_path(state),
  (report, codes) => {
    let statusList = report.resource;
    if (!statusList) return {...report};
    statusList = R.map(codeProjectFromArray(['status'], codes), statusList);
    return {...report, resource: statusList};
  }
);

const bidStatusSelector = statusSelector(state => state.reports.by_status.bids, state => codeTables.get('bidstatus'));
const projectStatusSelector = statusSelector(state => state.reports.by_status.projects, state => codeTables.get('projectstatus'));
const taskStatusSelector = statusSelector(state => state.reports.by_status.tasks, state => codeTables.get('taskstatus'));
const projectRfiStatusSelector = statusSelector(state => state.reports.by_status.projectrfis, state => codeTables.get('projectrfistatus'));
const submittalStatusSelector = statusSelector(state => state.reports.by_status.submittals, state => codeTables.get('submittalstatus'));
const punchStatusSelector = statusSelector(state => state.reports.by_status.punches, state => codeTables.get('punchlistitemstatus'));

const upcomingBidReportSelector = createSelector(
  state => state.reports.details.upcomingbids,
  report => ({
    ...report, resource: R.map(R.pipe(
      R.pick(['id', 'bidduedatetime', 'name', 'prebidmeetingrequired', 'prebidmeetingdatetime', 'lastaddendum']),
      (el) => {
        return {
          key: el.id,
          bidduedatetime: convertToDate(el.bidduedatetime),
          prebidmeetingrequired: el.prebidmeetingrequired,
          prebidmeetingdatetime: convertToDate(el.prebidmeetingdatetime),
          name: el.name,
          addendum: el.lastaddendum
        };
      }
    ), report.resource || {})
  })
);

const upcomingBidDetailsReportSelector = createSelector(
  state => state.reports.details.upcomingbids,
  (report) => ({
    ...report, resource: R.map(R.pipe(R.pick(['id', 'bidduedatetime', 'name', 'number',
        'address', 'address2', 'city', 'state', 'zip', 'country', 'addendums', 'prebidmeeting',
        'prebidmeetingdatetime', 'prebidmeetinglocation', 'prebidmeetingrequired', 'siteownername',
        'architectname', 'engineername', 'description']),
      (el) => {
        return {
          ...el,
          bidduedatetime: formatMoment(el.bidduedatetime),
          prebidmeetingdatetime: formatMoment(el.prebidmeetingdatetime),
        }
      }), report.resource)
  })
);

const projectsListSelector = createSelector(
  state => state.view.data,
  (projects) => R.map(R.pipe(R.view(R.lensPath(['resource'])), R.pick(['name', 'id'])), Object.values(projects))
);

const taskReportSelector = createSelector(
  state => state.reports.details.tasks,
  (report) => {
    return {
      ...report, resource: R.map(R.pipe(
        R.pick(['id', 'name', 'number', 'duedate', 'description', 'createdby', 'owner', 'datecreated', 'status']),
        (el) => {
          return {
            ...el,
            datecreated: formatMoment(el.datecreated),
            duedate: el.duedate || '',
          };
        },
        (el) => {
          return {
            key: el.id,
            title: el.name,
            overview: `Title: ${el.name}\nNumber: ${el.number}\nDue: ${el.duedate}\nQuestion: ${el.description}`,
            users: `Assigned to: ${el.owner}\nCreated by: ${el.createdby}\nOn: ${el.datecreated}`,
            status: el.status
          };
        }
      ), report.resource)
    };
  }
);

const projectRfiReportSelector = createSelector(
  state => state.reports.details.projectrfis,
  (report) => {
    return {
      ...report, resource: R.map(R.pipe(
        R.pick(['id', 'title', 'number', 'duedate', 'description', 'createdby', 'owner', 'datecreated', 'status']),
        (el) => {
          return {
            ...el,
            datecreated: formatMoment(el.datecreated),
            duedate: el.duedate || '',
          };
        },
        (el) => {
          return {
            key: el.id,
            title: el.title,
            overview: `Title: ${el.title}\nNumber: ${el.number}\nDue: ${el.duedate}\nQuestion: ${el.description}`,
            users: `Assigned to: ${el.owner}\nCreated by: ${el.createdby}\nOn: ${el.datecreated}`,
            status: el.status
          };
        }
      ), report.resource)
    };
  }
);

const submittalReportSelector = createSelector(
  state => state.reports.details.submittals,
  (report) => {
    return {
      ...report, resource: R.map(R.pipe(
        R.pick(['id', 'title', 'number', 'duedate', 'description', 'createdby', 'owner', 'datecreated', 'status']),
        (el) => {
          return {
            ...el,
            datecreated: formatMoment(el.datecreated),
            duedate: el.duedate || '',
          };
        },
        (el) => {
          return {
            key: el.id,
            title: el.title,
            overview: `Title: ${el.title}\nNumber: ${el.number}\nDue: ${el.duedate}\nQuestion: ${el.description}`,
            users: `Assigned to: ${el.owner}\nCreated by: ${el.createdby}\nOn: ${el.datecreated}`,
            status: el.status
          };
        }
      ), report.resource)
    };
  }
);

const dailyLogReportSelector = createSelector(
  state => state.reports.details.dailylogs,
  (report) => {
    return {
      ...report, resource: R.map(R.pipe(
        R.pick(['number', 'logdate', 'weather', 'templo', 'temphi', 'safetyissues', 'weatherdelays', 'tradedelays',
          'safetydelays', 'otherdelays', 'visitors', 'subcontractors', 'equipment', 'summary', 'csicodes',
          'datecreated', 'createdbyuserid', 'createdby']),
        (el) => {
          return {
            ...el,
            _datecreated: convertToDate(el.datecreated),
            datecreated: formatMoment(el.datecreated),
            _logdate: convertToLocalDate(el.logdate),
            logdate: formatMomentDate(el.logdate),
          };
        }
      ), report.resource)
    };
  }
);

const meetingMinutesReportSelector = createSelector(
  state => state.reports.details.meetingminutes,
  (report) => {
    return {
      ...report, resource: R.map(R.pipe(
        R.pick(['number', 'meetingdatetime', 'createdby', 'datecreated', 'meetingtype', 'attendees', 'safety', 'notes',
          'rfis', 'newbusiness', 'nextmeetingdatetime']),
        (el) => {
          return {
            ...el,
            _datecreated: convertToDate(el.datecreated),
            datecreated: formatMoment(el.datecreated),
            _meetingdatetime: convertToDate(el.meetingdatetime),
            meetingdatetime: formatMoment(el.meetingdatetime),
            _nextmeetingdatetime: convertToDate(el.nextmeetingdatetime),
            nextmeetingdatetime: formatMoment(el.nextmeetingdatetime),
          };
        }
      ), report.resource)
    };
  }
);



// WEBPACK FOOTER //
// ./src/components/Reports/ReportSelectorFactory.js